import React, {useCallback, useEffect, useState} from 'react';
import {Box, Dialog, Grid, Typography} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import WebsiteToolbar from './components/WebsiteToolbar';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {apiRootState} from '../../recoil/rootAtom';
import {loadingState} from '../../recoil/bidmiiCustomLoader/bidmiiCustomLoaderAtom';
import {API_URL} from '../../constants';
import {fetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import CompanySummary from './components/CompanySummary';
import useScreenWidth from '../../hooks/useScreenWidth';
import {theme} from '../../Theme';
import ProPastProjects from './components/ProPastProjects';
import ProAboutSection from './components/ProAboutSection';
import {LazyRequestQuoteDialog} from '../../common/LazyLoader';
import Anchor from '../../common/Anchor';
import * as Sentry from '@sentry/react';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiTypography-root': {
      fontFamily: 'roboto',
    },
    '& .MuiButton-root': {
      fontFamily: 'roboto',
    },
  },
  scrollDiv: {
    overflowY: 'scroll',
    height:
      window.innerWidth > 600 ? 'calc(100vh - 72px)' : 'calc(100vh - 114px)',
  },
  main: {
    maxWidth: 1024,
    margin: '0 auto',
    paddingBottom: 60,
    minHeight:
      window.innerWidth > 600 ? 'calc(100vh - 96px)' : 'calc(100vh - 57px)',
  },
  footer: {
    backgroundColor: theme.palette.gray.main,
    height: window.innerWidth > 600 ? 245 : 140,
  },
}));

export const ProWebsite = (props: $TSFixMe) => {
  const classes = useStyles();
  const width = useScreenWidth();

  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<any | null>(null);
  const apiRoot = useRecoilValue(apiRootState);
  const setGlobalLoading = useSetRecoilState(loadingState);

  const [pageValue, setPageValue] = useState('projects');
  const [projects, setProjects] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [projectsPage, setProjectsPage] = useState(0);
  const [reviewsPage, setReviewsPage] = useState(0);

  useEffect(() => {
    if (apiRoot) {
      setGlobalLoading(true);
      const url = (window as $TSFixMe).OVERRIDE_USER_ID
        ? `${API_URL}/users/${(window as $TSFixMe).OVERRIDE_USER_ID}`
        : // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
          apiRoot._links.publicUser.href.replace(
            '{userId}',
            props.match.params.id,
          );

      fetchRequest(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.json())
        .then((res) => {
          setGlobalLoading(false);
          setUser(res);
        });
    }
  }, [setUser, props.match.params.id, setGlobalLoading, apiRoot]);

  useEffect(() => {
    if (user) {
      document.title = `${user?.companyName} - ${
        pageValue === 'projects' ? 'Past Work' : 'About'
      }`;

      let link: any = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = user.logoImageUrl;
    }
  }, [pageValue, user]);

  const getProjects = useCallback(() => {
    if (user?.id) {
      setLoading(true);
      fetchRequest(
        `${API_URL}/users/${user.id}/rfps?userType=BIDDER&state=COMPLETE&page=${projectsPage}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
        .then((res) => res.json())
        .then((res) => {
          if (res._embedded) {
            const data = res?._embedded?.models;
            if (projectsPage > 0) {
              setProjects([...projects, ...(data || [])]);
            } else {
              setProjects(data);
            }

            if (res.page && projectsPage < res.page.totalPages - 1) {
              setProjectsPage((currentPage) => currentPage + 1);
            }
          }
        })
        .catch((err) => {
          process.env.NODE_ENV === 'development' && console.error(err);
          Sentry.withScope(function (scope) {
            scope.setTag('section', 'ProWebsite.tsx getProjects()');
            Sentry.captureException(err);
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsPage, user]);

  const getReviews = useCallback(() => {
    if (user?.id) {
      setLoading(true);
      fetchRequest(
        `${API_URL}/users/${user.id}/reviews?page=${reviewsPage}&pageSize=100`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
        .then((res) => res.json())
        .then((res) => {
          setLoading(false);
          if (res._embedded) {
            const data = res?._embedded?.models;
            if (reviewsPage > 0) {
              setReviews([...reviews, ...(data || [])]);
            } else {
              setReviews(data);
            }

            if (res.page && reviewsPage < res.page.totalPages - 1) {
              setReviewsPage((currentPage) => currentPage + 1);
            }
          }
        })
        .catch((err) => {
          process.env.NODE_ENV === 'development' && console.error(err);
          Sentry.withScope(function (scope) {
            scope.setTag('section', 'ProWebsite.tsx getReviews()');
            Sentry.captureException(err);
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewsPage, user]);

  useEffect(() => {
    getProjects();
  }, [getProjects]);

  useEffect(() => {
    getReviews();
  }, [getReviews]);

  return (
    <Dialog open fullScreen>
      {user && (
        <div className={classes.root}>
          <WebsiteToolbar
            user={user}
            pageValue={pageValue}
            setPageValue={setPageValue}
          />
          <div className={classes.scrollDiv} id="mainScrollDiv">
            <main className={classes.main}>
              <CompanySummary user={user} />

              {width <= 600 && (
                <Box mx={2.5}>
                  <LazyRequestQuoteDialog user={user} />
                </Box>
              )}

              {pageValue === 'projects' && (
                <ProPastProjects
                  user={user}
                  projects={projects}
                  reviews={reviews}
                  loading={loading}
                />
              )}
              {pageValue === 'about' && (
                <ProAboutSection
                  user={user}
                  reviews={reviews}
                  loading={loading}
                />
              )}
            </main>

            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              className={classes.footer}>
              <Typography
                style={{
                  fontFamily: 'roboto',
                  fontSize: width > 600 ? 18 : 12,
                  marginBottom: width > 600 ? 16 : 8,
                }}>
                POWERED BY
              </Typography>
              <Anchor href="https://bidmii.com">
                <img
                  onClick={() => window.open('https://bidmii.com', '_blank')}
                  src={'https://cdn.bidmii.com/media/web_assets/logo.webp'}
                  alt="bidmii"
                  style={{width: width > 600 ? 165 : 110, cursor: 'pointer'}}
                />
              </Anchor>
            </Grid>
          </div>
        </div>
      )}
    </Dialog>
  );
};
